import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Drawer, Typography, message } from 'antd';
import {
  CalendarOutlined,
  CoffeeOutlined,
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import TimeSelect from './TimeSelect';
import type { ScheduleBreakEntry, ScheduleDayEntry } from '../../../types/provider';

const { Text } = Typography;

const fmtTime = (t: string) => t.slice(0, 5);

interface Props {
  /** When non-null the sheet opens. */
  day: ScheduleDayEntry | null;
  dayLabel: string;
  breaks: ScheduleBreakEntry[];
  saving: boolean;
  breakSaving: boolean;
  onClose: () => void;
  onSaveHours: (startTime: string, endTime: string) => void | Promise<void>;
  onAddBreak: (startTime: string, endTime: string) => void | Promise<void>;
  onDeleteBreak: (b: ScheduleBreakEntry) => void;
}

export default function DayEditorSheet({
  day,
  dayLabel,
  breaks,
  saving,
  breakSaving,
  onClose,
  onSaveHours,
  onAddBreak,
  onDeleteBreak,
}: Props) {
  const { t } = useTranslation('provider');
  const isOpen = !!day;

  // Keep last day visible during close animation
  const [internalDay, setInternalDay] = useState<ScheduleDayEntry | null>(day);
  const [internalDayLabel, setInternalDayLabel] = useState<string>(dayLabel);

  useEffect(() => {
    if (day) {
      setInternalDay(day);
      setInternalDayLabel(dayLabel);
    }
  }, [day, dayLabel]);

  const handleAfterOpenChange = (visible: boolean) => {
    if (!visible) {
      setInternalDay(null);
      setStart('09:00');
      setEnd('18:00');
      setAddingBreak(false);
      setNewBreakStart('13:00');
      setNewBreakEnd('14:00');
    }
  };

  // Form state
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('18:00');
  const [addingBreak, setAddingBreak] = useState(false);
  const [newBreakStart, setNewBreakStart] = useState('13:00');
  const [newBreakEnd, setNewBreakEnd] = useState('14:00');
  const [saveBtnPressed, setSaveBtnPressed] = useState(false);
  const [addBreakBtnPressed, setAddBreakBtnPressed] = useState(false);

  // Initialise form values when a new day opens
  useEffect(() => {
    if (day) {
      setStart(fmtTime(day.startTime ?? '09:00'));
      setEnd(fmtTime(day.endTime ?? '18:00'));
      setAddingBreak(false);
    }
  }, [day]);

  // Telegram BackButton wiring
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => onClose();
    WebApp.BackButton.show();
    WebApp.BackButton.onClick(handler);
    return () => {
      WebApp.BackButton.offClick(handler);
      WebApp.BackButton.hide();
    };
  }, [isOpen, onClose]);

  if (!internalDay) return null;

  const accent = '#3390ec';
  const accentDark = '#1677ff';
  const lockSave = saving || breakSaving;

  const handleSaveHours = () => {
    if (start >= end) {
      message.warning(t('schedule.dayModal.startMustBeBeforeEnd'));
      return;
    }
    void onSaveHours(start, end);
  };

  const handleAddBreak = () => {
    if (newBreakStart >= newBreakEnd) {
      message.warning(t('schedule.breaks.startMustBeBeforeEnd'));
      return;
    }
    void onAddBreak(newBreakStart, newBreakEnd);
  };

  return (
    <Drawer
      open={isOpen}
      placement="bottom"
      onClose={onClose}
      closable={false}
      maskClosable={!lockSave}
      height="auto"
      afterOpenChange={handleAfterOpenChange}
      styles={{
        body: {
          padding: 0,
          background: 'var(--bl-surface-soft, #f7f8fa)',
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
        },
        wrapper: {
          borderRadius: '20px 20px 0 0',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 32px)',
        },
        mask: { background: 'rgba(15,23,42,0.45)' },
      }}
    >
      {/* Drag handle area */}
      <div
        style={{
          flexShrink: 0,
          padding: '10px 0 8px',
          background: 'var(--bl-surface, #fff)',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: 'var(--bl-divider, #e5e7eb)',
            margin: '0 auto',
          }}
        />
      </div>

      {/* Hero header */}
      <div
        style={{
          flexShrink: 0,
          position: 'relative',
          padding: '12px 20px 16px',
          background: `linear-gradient(135deg, ${accent}10 0%, ${accent}1c 60%, ${accent}08 100%)`,
          borderBottom: `1px solid ${accent}14`,
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -40,
            right: -30,
            width: 130,
            height: 130,
            borderRadius: '50%',
            background: `${accent}1f`,
            filter: 'blur(6px)',
            opacity: 0.55,
          }}
        />

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
              color: '#fff',
              fontSize: 19,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: `0 4px 12px ${accent}38, inset 0 1px 0 rgba(255,255,255,0.22)`,
            }}
          >
            <CalendarOutlined />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--bl-text-muted, #6b7280)',
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                marginBottom: 2,
              }}
            >
              {t('schedule.weekly.editHint')}
            </Text>
            <Text
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: 'var(--bl-text-strong, #0f172a)',
                letterSpacing: -0.4,
                lineHeight: 1.1,
                display: 'block',
              }}
            >
              {internalDayLabel}
            </Text>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={lockSave}
            aria-label="Close"
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.85)',
              color: 'var(--bl-text-muted, #6b7280)',
              cursor: lockSave ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              WebkitTapHighlightColor: 'transparent',
              flexShrink: 0,
            }}
          >
            <CloseOutlined />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '14px 16px calc(20px + env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {/* Working hours card */}
        <div
          style={{
            background: 'var(--bl-surface, #fff)',
            border: '1px solid var(--bl-divider, #ebebeb)',
            borderRadius: 14,
            padding: '14px 14px',
          }}
        >
          <Text
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--bl-text-muted, #9ca3af)',
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              marginBottom: 10,
            }}
          >
            {t('schedule.dayModal.hoursSection')}
          </Text>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel>{t('schedule.dayModal.startLabel')}</FieldLabel>
              <TimeSelect value={start} onChange={setStart} />
            </div>
            <Text style={{ flexShrink: 0, paddingBottom: 10, color: 'var(--bl-text-muted, #9ca3af)' }}>
              →
            </Text>
            <div style={{ flex: 1 }}>
              <FieldLabel>{t('schedule.dayModal.endLabel')}</FieldLabel>
              <TimeSelect value={end} onChange={setEnd} />
            </div>
          </div>

          <button
            type="button"
            onClick={handleSaveHours}
            disabled={lockSave}
            onTouchStart={() => !lockSave && setSaveBtnPressed(true)}
            onTouchEnd={() => setSaveBtnPressed(false)}
            onTouchCancel={() => setSaveBtnPressed(false)}
            onMouseDown={() => !lockSave && setSaveBtnPressed(true)}
            onMouseUp={() => setSaveBtnPressed(false)}
            onMouseLeave={() => setSaveBtnPressed(false)}
            style={{
              width: '100%',
              minHeight: 44,
              padding: '0 16px',
              border: 'none',
              borderRadius: 12,
              background: 'linear-gradient(135deg, #3390ec 0%, #1677ff 100%)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: lockSave ? 'not-allowed' : 'pointer',
              opacity: lockSave ? 0.85 : 1,
              boxShadow: '0 4px 12px rgba(22,119,255,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
              transform: saveBtnPressed && !lockSave ? 'scale(0.985)' : 'scale(1)',
              transition: 'transform 0.1s ease, opacity 0.15s ease',
              WebkitTapHighlightColor: 'transparent',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          >
            {saving && <LoadingOutlined spin />}
            <span>{t('schedule.dayModal.saveBtn')}</span>
          </button>
        </div>

        {/* Breaks card */}
        <div
          style={{
            background: 'var(--bl-surface, #fff)',
            border: '1px solid var(--bl-divider, #ebebeb)',
            borderRadius: 14,
            padding: '14px 14px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, rgba(250,140,22,0.16) 0%, rgba(250,140,22,0.32) 100%)',
                  border: '1px solid rgba(250,140,22,0.18)',
                  color: '#d46b08',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  flexShrink: 0,
                }}
              >
                <CoffeeOutlined />
              </div>
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--bl-text-strong, #0f172a)',
                }}
              >
                {t('schedule.breaks.title')}
              </Text>
            </div>

            {!addingBreak && internalDay.workingHoursId != null && (
              <button
                type="button"
                onClick={() => setAddingBreak(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  background: 'var(--bl-accent-soft, #e7f1fe)',
                  border: '1px solid rgba(51,144,236,0.22)',
                  color: 'var(--bl-accent, #1677ff)',
                  borderRadius: 8,
                  padding: '4px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              >
                <PlusOutlined style={{ fontSize: 10 }} />
                {t('schedule.breaks.addBtn')}
              </button>
            )}
          </div>

          {/* Existing breaks */}
          {breaks.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: addingBreak ? 12 : 0 }}>
              {breaks.map((b) => (
                <div
                  key={b.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    background: 'rgba(250,140,22,0.06)',
                    border: '1px solid rgba(250,140,22,0.16)',
                    borderRadius: 10,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#d46b08',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {fmtTime(b.startTime)} – {fmtTime(b.endTime)}
                  </Text>
                  <button
                    type="button"
                    onClick={() => onDeleteBreak(b)}
                    aria-label="Delete"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      border: 'none',
                      background: 'transparent',
                      color: '#9ca3af',
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                      outline: 'none',
                      fontSize: 13,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = '#ef4444';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af';
                    }}
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {breaks.length === 0 && !addingBreak && (
            <Text
              style={{
                fontSize: 12,
                color: 'var(--bl-text-muted, #9ca3af)',
                textAlign: 'center',
                display: 'block',
                padding: '6px 0 2px',
              }}
            >
              {t('schedule.breaks.empty')}
            </Text>
          )}

          {/* Add break form */}
          {addingBreak && (
            <div
              style={{
                background: 'var(--bl-surface-soft, #f7f8fa)',
                border: '1px solid var(--bl-divider, #e5e7eb)',
                borderRadius: 12,
                padding: '12px 12px',
                marginTop: breaks.length > 0 ? 0 : 8,
              }}
            >
              <Text
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--bl-text-muted, #9ca3af)',
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 8,
                }}
              >
                {t('schedule.breaks.newTitle')}
              </Text>

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <FieldLabel>{t('schedule.dayModal.startLabel')}</FieldLabel>
                  <TimeSelect value={newBreakStart} onChange={setNewBreakStart} />
                </div>
                <Text style={{ flexShrink: 0, paddingBottom: 10, color: 'var(--bl-text-muted, #9ca3af)' }}>
                  →
                </Text>
                <div style={{ flex: 1 }}>
                  <FieldLabel>{t('schedule.dayModal.endLabel')}</FieldLabel>
                  <TimeSelect value={newBreakEnd} onChange={setNewBreakEnd} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={handleAddBreak}
                  disabled={breakSaving}
                  onTouchStart={() => !breakSaving && setAddBreakBtnPressed(true)}
                  onTouchEnd={() => setAddBreakBtnPressed(false)}
                  onTouchCancel={() => setAddBreakBtnPressed(false)}
                  onMouseDown={() => !breakSaving && setAddBreakBtnPressed(true)}
                  onMouseUp={() => setAddBreakBtnPressed(false)}
                  onMouseLeave={() => setAddBreakBtnPressed(false)}
                  style={{
                    flex: 1,
                    minHeight: 40,
                    border: 'none',
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: breakSaving ? 'not-allowed' : 'pointer',
                    opacity: breakSaving ? 0.85 : 1,
                    boxShadow: '0 3px 10px rgba(250,140,22,0.32), inset 0 1px 0 rgba(255,255,255,0.18)',
                    transform: addBreakBtnPressed && !breakSaving ? 'scale(0.985)' : 'scale(1)',
                    transition: 'transform 0.1s ease',
                    WebkitTapHighlightColor: 'transparent',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    fontFamily: 'inherit',
                    outline: 'none',
                  }}
                >
                  {breakSaving && <LoadingOutlined spin />}
                  <span>{t('schedule.breaks.addBtn')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setAddingBreak(false)}
                  disabled={breakSaving}
                  style={{
                    flex: 1,
                    minHeight: 40,
                    border: 'none',
                    borderRadius: 10,
                    background: 'var(--bl-surface, #fff)',
                    color: 'var(--bl-text-muted, #6b7280)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: breakSaving ? 'not-allowed' : 'pointer',
                    opacity: breakSaving ? 0.55 : 1,
                    WebkitTapHighlightColor: 'transparent',
                    fontFamily: 'inherit',
                    outline: 'none',
                  }}
                >
                  {t('schedule.breaks.cancelBtn')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <Text
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--bl-text-muted, #9ca3af)',
        display: 'block',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 5,
      }}
    >
      {children}
    </Text>
  );
}
